import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lightning, Trophy, CalendarStar, Sparkle, ArrowRight, Bell, BellSlash } from "@phosphor-icons/react";
import { scheduleReminder, removeReminder, hasReminder, isSupported, permission } from "@/lib/notify";

/** Compact mode-countdown strip for the Home page. Live-ticks every second.
 *  Renders a row of 3-4 cards (Daily / Ladder / Saturday / Championship-if-active),
 *  each showing the mode label + a ticking countdown to the next start time.
 *  Clicking a card navigates to that mode's page.
 */
export default function ModeCountdowns() {
  const [data, setData] = useState(null);
  const [drift, setDrift] = useState(0); // server time - client time
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/modes/countdowns");
        if (!alive) return;
        if (r.data?.server_now) {
          setDrift(new Date(r.data.server_now).getTime() - Date.now());
        }
        setData(r.data);
      } catch { /* silent */ }
    };
    load();
    const reload = setInterval(load, 60000);
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => { alive = false; clearInterval(reload); clearInterval(t); };
  }, []);

  if (!data) return null;

  const items = [
    { key: "daily", label: "NK Daily", to: "/daily", Icon: Lightning, info: data.daily, accent: "accent" },
    { key: "saturday", label: "Saturday", to: "/saturday", Icon: CalendarStar, info: data.saturday, accent: "gold" },
  ];
  if (data.championship) {
    items.push({ key: "championship", label: "Championship", to: "/championship", Icon: Sparkle, info: data.championship, accent: "gold" });
  }

  return (
    <div className="space-y-2" data-testid="mode-countdowns">
      <p className="eyebrow flex items-center justify-between">
        <span>Next up</span>
        <span className="text-[10px] text-nk-dim font-mono normal-case tracking-normal">live</span>
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
        {items.map((it) => {
          const { key: itemKey, ...itemProps } = it;
          return <CountdownCard key={itemKey} {...itemProps} nowMs={nowMs} drift={drift} />;
        })}
      </div>
    </div>
  );
}

function CountdownCard({ label, to, Icon, info, accent, nowMs, drift }) {
  const isLive = info?.state === "RUNNING";
  const isScheduled = info?.state === "SCHEDULED";
  const isGold = accent === "gold";
  const isDisabled = info && !info.enabled;

  const { countdown, sub } = computeCountdown({ info, nowMs, drift, isLive, isScheduled, isDisabled });

  const reminderKey = info?.next_at ? `mode:${label}:${info.next_at}` : null;
  const [reminded, setReminded] = useState(() => reminderKey ? hasReminder(reminderKey) : false);

  const toggleReminder = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!reminderKey) return;
    if (!isSupported()) {
      toast.error("This browser doesn't support notifications.");
      return;
    }
    if (reminded) {
      removeReminder(reminderKey);
      setReminded(false);
      toast.message(`Reminder removed for ${label}`);
      return;
    }
    const result = await scheduleReminder({
      key: reminderKey,
      title: `${label} is starting`,
      body: `Tap to jump in.`,
      fireAt: info.next_at,
    });
    if (result.ok) {
      setReminded(true);
      toast.success(`We'll ping you when ${label} starts.`);
    } else if (result.reason === "denied") {
      toast.error("Notifications blocked. Enable them in your browser settings.");
    } else {
      toast.error("Couldn't set reminder.");
    }
  };

  const baseColor = isGold ? "text-nk-gold" : "text-nk-accent";
  const borderClr = isLive ? "border-nk-bad/60" : isGold ? "border-nk-gold/40" : "border-nk-line hover:border-nk-accent/60";
  const canRemind = !isLive && info?.next_at && !isDisabled;

  return (
    <Link
      to={to}
      data-testid={`countdown-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`nk-card p-3 border ${borderClr} relative overflow-hidden transition-colors active:scale-[0.98]`}
    >
      <div className="flex items-center justify-between mb-1.5 gap-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <Icon size={12} weight={isGold ? "fill" : "duotone"} className={`shrink-0 ${baseColor}`} />
          <p className="text-[11px] font-medium truncate">{label}</p>
        </div>
        {isLive ? (
          <span className="text-[9px] text-nk-bad font-mono inline-flex items-center gap-1 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-nk-bad animate-pulse-dot" />LIVE
          </span>
        ) : canRemind ? (
          <button
            onClick={toggleReminder}
            data-testid={`remind-${label.toLowerCase().replace(/\s/g, "-")}`}
            aria-label={reminded ? "Cancel reminder" : "Remind me"}
            title={reminded ? "Reminder set — tap to cancel" : "Remind me when this opens"}
            className={`p-1 rounded-sm transition-colors shrink-0 ${
              reminded ? "text-nk-accent" : "text-nk-mute hover:text-nk-text"
            }`}
          >
            {reminded ? <Bell size={11} weight="fill" /> : <BellSlash size={11} weight="regular" />}
          </button>
        ) : null}
      </div>
      <p className={`font-display text-xl font-medium tabular-nums ${isLive ? "text-nk-bad" : baseColor}`}>
        {countdown}
      </p>
      <p className="text-[10px] text-nk-dim mt-0.5 flex items-center gap-1">
        <span className="truncate">{sub}</span>
        <ArrowRight size={9} className="opacity-50 shrink-0 ml-auto" />
      </p>
    </Link>
  );
}

function computeCountdown({ info, nowMs, drift, isLive, isScheduled, isDisabled }) {
  if (isDisabled) return { countdown: "Offline", sub: "—" };
  if (!info?.next_at) return { countdown: "—", sub: "" };
  const targetMs = new Date(info.next_at).getTime();
  const remainingSec = Math.max(0, Math.floor((targetMs - (nowMs + drift)) / 1000));
  const countdown = formatRemaining(remainingSec);
  const sub = isLive ? "Live — join now" : isScheduled ? "Show locked in" : "Next round";
  return { countdown, sub };
}

function formatRemaining(seconds) {
  if (seconds <= 0) return "Now";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
